import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ResolveThunks } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';

import type { MemberViewModel } from 'ab-viewModels/member.viewModel';

import { formatPhoneNumber } from '@acceligentllc/shared/utils/phone';

import { AccountPermissionTemplateLabel } from '@acceligentllc/shared/enums/accountPermissionTemplateOptions';
import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { UserPermission } from '@acceligentllc/shared/enums/userPermission';

import TableNameEnum from 'ab-enums/tableName.enum';

import { DEFAULT_EMPLOYEE_IMAGE } from 'ab-common/constants/value';
import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ImageTag from 'af-components/Image';
import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import type { TableProps } from 'af-components/Table/types';
import DateCell from 'af-components/Table6/Cells/DateCell';
import StatusCell from 'af-components/Table6/Cells/StatusCell';

import * as CompanyActions from 'af-actions/companies';

interface PathParams {
	clientAlias: string;
}

interface DispatchProps {
	getMembersForOrganization: typeof CompanyActions.getMembersForOrganizationByAlias;
	removeOrganizationAdminPrivileges: typeof CompanyActions.removeOrganizationAdminPrivileges;
	giveOrganizationAdminPrivileges: typeof CompanyActions.giveOrganizationAdminPrivileges;
}

type OwnProps = CustomRouteComponentProps<PathParams>;
type Props = ResolveThunks<DispatchProps> & OwnProps;

const adminRoles = [UserPermission.ADMIN, UserPermission.OWNER];

const CompanyMembersList: React.FC<Props> = (props) => {
	const tableRef = React.useRef<TableRef<MemberViewModel>>(null);

	const changeAdminPrivileges = React.useCallback(() => async (original: MemberViewModel) => {
		const { removeOrganizationAdminPrivileges, giveOrganizationAdminPrivileges, match: { params: { clientAlias } } } = props;

		if (adminRoles.includes(original.role)) {
			await removeOrganizationAdminPrivileges(original.userId.toString(), clientAlias);
		} else {
			await giveOrganizationAdminPrivileges(original.userId.toString(), clientAlias);
		}
	}, [props]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<MemberViewModel, unknown>) => {
		const actions = [
			{
				label: adminRoles.includes(_cell.row.original.role) ? 'Remove platform admin' : 'Make platform admin',
				action: changeAdminPrivileges,
				shouldRefresh: true,
			},
		];

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [changeAdminPrivileges]);

	const getMembers = React.useCallback((tableRequestModel: TableQuery) => {
		const { getMembersForOrganization, match: { params: { clientAlias } } } = props;

		return getMembersForOrganization(tableRequestModel, clientAlias);
	}, [props]);

	const breadcrumbs = React.useMemo(() => [{ label: 'Users' }], []);

	const columns: TableProps<MemberViewModel>['columns'] = React.useMemo(() => [
		{
			id: 'image',
			accessor: 'imageUrl',
			enableSorting: false,
			header: () => <EmptyCell />,
			size: 100,
			cell: ({ cell }) => (
				<div className="rt-thumbnail">
					<ImageTag
						fallbackSrc={DEFAULT_EMPLOYEE_IMAGE}
						minSize={BlobStorageImageSizeContainer.SIZE_50X50}
						src={cell.row.original.imageUrl ?? null}
						tryOriginal={true}
						tryRoot={true}
					/>
				</div>
			),
		},
		{
			id: 'uniqueId',
			header: 'Unique ID',
			accessor: 'uniqueId',
			enableSorting: true,
			cell: ({ cell }) => <div className="text-strong">
				{cell.row.original.uniqueId}
			</div>,
		},
		{
			id: 'fullName',
			header: 'Full Name',
			accessor: 'fullName',
			enableSorting: true,
			cell: ({ cell }) => <div className="text-strong">{cell.row.original.fullName}</div>,
		},
		{
			id: 'email',
			header: 'Email Address',
			accessor: 'email',
			enableSorting: true,
			cell: ({ cell }) => cell.row.original.email,
		},
		{
			id: 'phoneNumber',
			header: 'Mobile Phone',
			accessor: 'phoneNumber',
			enableSorting: true,
			cell: ({ cell }) => formatPhoneNumber(cell.row.original.phoneNumber, cell.row.original.countryCode),
		},
		{
			id: 'role',
			header: 'Role',
			accessor: 'role',
			enableSorting: true,
			cell: ({ cell }) => cell.row.original.role,
		},
		{
			id: 'userRole',
			header: 'User Role',
			accessor: 'accountTemplate',
			enableSorting: true,
			cell: ({ cell }) => <strong>{AccountPermissionTemplateLabel[cell.row.original.accountTemplate]}</strong>,
		},
		{
			id: 'homeOffice',
			header: 'Home Office',
			accessor: 'location.nickname',
			enableSorting: true,
			cell: ({ cell }) => cell.row.original.location?.nickname ?? <EmptyCell />,
		},
		{
			id: 'lastLoginAt',
			header: 'Last Login',
			accessor: 'lastLoginAt',
			enableSorting: true,
			cell: ({ cell }) => cell.row.original.lastLoginAt
				? <DateCell date={cell.row.original.lastLoginAt} format={TimeFormat.DATE_ONLY} isLeftAligned={true} withTime={true} />
				: <EmptyCell />,
		},
		{
			id: 'status',
			header: 'Status',
			accessor: 'status',
			enableSorting: true,
			cell: ({ cell }) => <StatusCell {...cell.row.original} />,
		},
		{
			id: 'activeInLMS',
			header: 'LMS access',
			accessor: 'activeInLMS',
			enableSorting: true,
			cell: ({ cell }) => {
				if (cell.row.original.activeInLMS) {
					return (<span className="text-green">Yes</span>);
				}
				return (<span className="text-grey">No</span>);
			},
		},
		{
			id: 'subscriptionStatus',
			header: 'SMS Subscribed Status',
			accessor: 'subscriptionStatus',
			enableSorting: false,
			cell: ({ cell }) => cell.row.original.subscriptionStatus,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [resolveActionsButton]);

	return (
		<div className="form-segment form-segment--maxi">
			<Breadcrumbs items={breadcrumbs} />
			<TableNew
				columns={columns}
				fetch={getMembers}
				hasSearchInput={true}
				hasSubRows={false}
				ref={tableRef}
				searchLabel="Members"
				selectable={true}
				tableName={TableNameEnum.PLATFORM_MEMBERS}
			/>
		</div>
	);
};

function mapDispatchToProps(): DispatchProps {
	return {
		getMembersForOrganization: CompanyActions.getMembersForOrganizationByAlias,
		removeOrganizationAdminPrivileges: CompanyActions.removeOrganizationAdminPrivileges,
		giveOrganizationAdminPrivileges: CompanyActions.giveOrganizationAdminPrivileges,
	};
}

const connector = connect<null, DispatchProps, OwnProps>(null, mapDispatchToProps());

export default connector(CompanyMembersList);
